import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Gift, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const LB = {
  sk: {
    first200: "Prvých 1000 zdarma:",
    remaining: "zostáva",
    tag: "ŠTARTOVACÍ DARČEK",
    limited: "limitovaná ponuka",
    headlinePre: "Prvých",
    headlineMid: "registrácií",
    headlineGets: "získa",
    headlineDays: "mesiac",
    headlineInActive: "v pláne PRO",
    headlineFree: "úplne zadarmo",
    headlineEnd: ".",
    sub: "Žiadna karta, žiadne podmienky — len sa zaregistruj. Po 30 dňoch sa môžeš rozhodnúť, či pokračovať. Akcia platí do 31. 12. 2026.",
    registered: "zaregistrovaných",
    seatsLeft: "miest zostáva",
  },
  en: {
    first200: "First 1000 free:",
    remaining: "left",
    tag: "LAUNCH GIFT",
    limited: "limited offer",
    headlinePre: "The first",
    headlineMid: "registrations",
    headlineGets: "get",
    headlineDays: "a month",
    headlineInActive: "on the PRO plan",
    headlineFree: "completely free",
    headlineEnd: ".",
    sub: "No card, no strings — just sign up. After 30 days you decide whether to continue. Offer valid until 31 Dec 2026.",
    registered: "registered",
    seatsLeft: "seats left",
  },
  cs: {
    first200: "Prvních 1000 zdarma:",
    remaining: "zbývá",
    tag: "STARTOVACÍ DÁREK",
    limited: "omezená nabídka",
    headlinePre: "Prvních",
    headlineMid: "registrací",
    headlineGets: "získá",
    headlineDays: "měsíc",
    headlineInActive: "v plánu PRO",
    headlineFree: "úplně zdarma",
    headlineEnd: ".",
    sub: "Žádná karta, žádné podmínky — jen se zaregistruj. Po 30 dnech se můžeš rozhodnout, zda pokračovat. Akce platí do 31. 12. 2026.",
    registered: "zaregistrovaných",
    seatsLeft: "míst zbývá",
  },
  de: {
    first200: "Erste 1000 gratis:",
    remaining: "übrig",
    tag: "LAUNCH-GESCHENK",
    limited: "begrenztes Angebot",
    headlinePre: "Die ersten",
    headlineMid: "Registrierungen",
    headlineGets: "erhalten",
    headlineDays: "einen Monat",
    headlineInActive: "im PRO-Plan",
    headlineFree: "komplett kostenlos",
    headlineEnd: ".",
    sub: "Keine Karte, keine Bedingungen — einfach registrieren. Nach 30 Tagen entscheidest du, ob du fortfährst. Aktion gültig bis 31.12.2026.",
    registered: "registriert",
    seatsLeft: "Plätze übrig",
  },
  pl: {
    first200: "Pierwsze 1000 darmowych:",
    remaining: "pozostało",
    tag: "PREZENT POWITALNY",
    limited: "oferta ograniczona",
    headlinePre: "Pierwsze",
    headlineMid: "rejestracji",
    headlineGets: "otrzymuje",
    headlineDays: "miesiąc",
    headlineInActive: "w planie PRO",
    headlineFree: "całkowicie za darmo",
    headlineEnd: ".",
    sub: "Bez karty, bez zobowiązań — po prostu się zarejestruj. Po 30 dniach zdecydujesz, czy kontynuować. Promocja ważna do 31.12.2026.",
    registered: "zarejestrowanych",
    seatsLeft: "miejsc pozostało",
  },
  fr: {
    first200: "Les 1000 premiers gratuits :",
    remaining: "restants",
    tag: "CADEAU DE DÉMARRAGE",
    limited: "offre limitée",
    headlinePre: "Les",
    headlineMid: "premières inscriptions",
    headlineGets: "obtiennent",
    headlineDays: "un mois",
    headlineInActive: "sur le plan PRO",
    headlineFree: "entièrement gratuit",
    headlineEnd: ".",
    sub: "Aucune carte, aucune condition — inscris-toi simplement. Après 30 jours, tu peux décider de continuer. Offre valable jusqu'au 31/12/2026.",
    registered: "inscrits",
    seatsLeft: "places restantes"
},
};

/**
 * Launch promo banner — shows first-1000-free counter.
 * Hides itself when promo is exhausted (remaining=0) unless `alwaysShow` is true.
 * Hidden for logged-in users (they already got the promo or don't need it).
 */
export default function LaunchPromoBanner({ alwaysShow = false, compact = false }) {
  const { user } = useAuth();
  const { lang } = useLang() || { lang: "sk" };
  const tr = LB[lang] || LB.sk;
  const [s, setS] = useState(null);
  useEffect(() => {
    if (user) return; // skip API call for logged-in users
    api.get("/launch-stats").then((r) => setS(r.data)).catch(() => {});
    const id = setInterval(() => {
      api.get("/launch-stats").then((r) => setS(r.data)).catch(() => {});
    }, 30000); // refresh every 30s
    return () => clearInterval(id);
  }, [user]);

  if (user) return null; // hide for logged-in users
  if (!s) return null;
  if (!s.promo_active && !alwaysShow) return null;

  const filledPct = Math.min(100, (s.registered_count / s.limit) * 100);

  if (compact) {
    return (
      <div data-testid="launch-promo-compact" className="border border-gold-700/40 bg-gold-500/5 p-3 text-xs">
        <div className="flex items-center gap-2 mb-2">
          <Gift className="w-4 h-4 text-gold-400" />
          <strong className="text-gold-300">{tr.first200}</strong>
          <span className="font-mono text-zinc-400">{s.registered_count} / {s.limit}</span>
          <span className="ml-auto text-gold-400 font-bold">{s.remaining} {tr.remaining}</span>
        </div>
        <div className="h-1.5 bg-zinc-800 overflow-hidden">
          <div className="h-full bg-gold-500" style={{ width: `${filledPct}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="launch-promo-banner"
      className="border border-gold-600 bg-gradient-to-r from-gold-500/10 via-gold-500/15 to-gold-500/10 p-5 md:p-6"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-gold-500/15 border border-gold-600 flex items-center justify-center flex-shrink-0">
          <Gift className="w-6 h-6 text-gold-400" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="wb-tag !text-gold-300 !border-gold-500">{tr.tag}</span>
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{tr.limited}</span>
          </div>
          <h3 className="font-display font-black text-xl md:text-2xl text-white leading-tight">
            {tr.headlinePre} <span className="text-gold-400">{s.limit} {tr.headlineMid}</span> {tr.headlineGets} <span className="text-gold-400">{tr.headlineDays}</span> {tr.headlineInActive} <span className="text-gold-400">{tr.headlineFree}</span>{tr.headlineEnd}
          </h3>
          <p className="text-xs text-zinc-400 mt-2">
            {tr.sub}
          </p>

          <div className="mt-4">
            <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider mb-1.5">
              <span className="text-zinc-400 inline-flex items-center gap-1"><Users className="w-3 h-3" /> {s.registered_count} / {s.limit} {tr.registered}</span>
              <span className="text-gold-400 font-bold">{s.remaining} {tr.seatsLeft}</span>
            </div>
            <div className="h-2 bg-zinc-900 border border-zinc-800 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-gold-600 to-gold-400 transition-all duration-700"
                style={{ width: `${filledPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
